"use client";

import ImageWithSkeleton from "@/components/ui/ImageWithSkeleton";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { ICertificate } from "@/types";
import { AnimatePresence, motion } from "framer-motion";
import { Award, CheckCircle, ExternalLink, X } from "lucide-react";
import { FC, useEffect, useRef } from "react";

interface CertificateModalProps {
    certificate: ICertificate | null;
    onClose: () => void;
}

const CertificateModal: FC<CertificateModalProps> = ({
    certificate,
    onClose,
}) => {
    const backdropRef = useRef<HTMLDivElement>(null);
    const { signedUrl, isLoading } = useSignedUrl(certificate?.image?._id);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [onClose]);

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === backdropRef.current) {
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {certificate && (
                <motion.div
                    ref={backdropRef}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleBackdropClick}
                >
                    <motion.div
                        className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-background-color p-4 shadow-xl sm:p-6"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 25,
                        }}
                    >
                        <div className="flex items-center justify-between border-b border-border-color pb-3">
                            <div className="flex items-center gap-2">
                                <Award className="size-6 text-primary" />
                                <h2 className="text-lg font-bold text-primary sm:text-xl">
                                    Certificate Preview
                                </h2>
                            </div>
                            <button
                                onClick={onClose}
                                className="rounded-full bg-primary p-2 transition hover:bg-primary/70"
                            >
                                <X className="size-5 text-text-color" />
                            </button>
                        </div>

                        <div className="my-4 flex flex-1 flex-col gap-4 overflow-y-auto">
                            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl border border-border-color bg-card-bg">
                                <ImageWithSkeleton
                                    src={signedUrl}
                                    alt={certificate.title}
                                    isLoadingUrl={isLoading}
                                    fill
                                    containerClassName="relative aspect-[4/3] w-full"
                                    className="h-full w-full object-contain p-2"
                                />
                            </div>

                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="space-y-1">
                                    <h3 className="text-xl font-bold text-primary">
                                        {certificate.title}
                                    </h3>
                                    <div className="flex items-center gap-1.5 text-xs font-medium text-text-color">
                                        <CheckCircle className="size-4 text-primary" />
                                        <span>Verified Credential</span>
                                    </div>
                                </div>

                                {certificate.verificationUrl && (
                                    <a
                                        href={certificate.verificationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-pill-primary flex items-center gap-2 text-xs sm:text-sm"
                                    >
                                        <ExternalLink className="size-4" />
                                        <span>Verify Credential</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CertificateModal;
